import { useState, useCallback } from 'react'
import { AgentManagementService } from '@/services/api/services/AgentManagementService'
import type { InvitationResponse } from '@/services/api/models/InvitationResponse'
import type { SharedAgentResponse } from '@/services/api/models/SharedAgentResponse'
import type { PermissionResponse } from '@/services/api/models/PermissionResponse'

export interface UseAgentSharingReturn {
  // Invitations
  invitations: InvitationResponse[]
  invitationsLoading: boolean
  invitationsError: string | null
  fetchInvitations: (statusFilter?: 'pending' | 'accepted' | 'rejected' | 'expired') => Promise<InvitationResponse[]>
  
  // Shared Agents
  sharedAgents: SharedAgentResponse[]
  sharedAgentsLoading: boolean
  sharedAgentsError: string | null
  fetchSharedAgents: () => Promise<SharedAgentResponse[]>
  leaveSharedAgent: (agentId: string) => Promise<void>
  
  // Permissions
  getAgentPermissions: (agentId: string) => Promise<PermissionResponse[]>
  
  // Outgoing Invitations  
  getOutgoingInvitations: (agentId: string) => Promise<InvitationResponse[]>
  revokeInvitation: (agentId: string, invitationToken: string) => Promise<void>
  
  // Utility
  refreshAll: () => Promise<void>
  clearErrors: () => void
}

export function useAgentSharing(): UseAgentSharingReturn {
  const [invitations, setInvitations] = useState<InvitationResponse[]>([])
  const [invitationsLoading, setInvitationsLoading] = useState(false)
  const [invitationsError, setInvitationsError] = useState<string | null>(null)
  
  const [sharedAgents, setSharedAgents] = useState<SharedAgentResponse[]>([])
  const [sharedAgentsLoading, setSharedAgentsLoading] = useState(false)
  const [sharedAgentsError, setSharedAgentsError] = useState<string | null>(null)

  const fetchInvitations = useCallback(async (statusFilter?: 'pending' | 'accepted' | 'rejected' | 'expired') => {
    try {
      setInvitationsLoading(true)
      setInvitationsError(null)
      
      const response = await AgentManagementService.listMyInvitations({
        statusFilter
      })
      
      setInvitations(response)
      return response
    } catch (err: unknown) {
      console.error('Failed to fetch invitations:', err)
      setInvitationsError(err instanceof Error ? err.message : 'Failed to load invitations')
      return []
    } finally {
      setInvitationsLoading(false)
    }
  }, [])

  const fetchSharedAgents = useCallback(async () => {
    try {
      setSharedAgentsLoading(true)
      setSharedAgentsError(null)
      
      const response = await AgentManagementService.listSharedAgents({})
      setSharedAgents(response)
      return response
    } catch (err: unknown) {
      console.error('Failed to fetch shared agents:', err)
      setSharedAgentsError(err instanceof Error ? err.message : 'Failed to load shared agents')
      return []
    } finally {
      setSharedAgentsLoading(false)
    }
  }, [])

  const getAgentPermissions = useCallback(async (agentId: string): Promise<PermissionResponse[]> => {
    try {
      return await AgentManagementService.listAgentPermissions({
        agentId
      })
    } catch (err: unknown) {
      console.error('Failed to fetch agent permissions:', err)
      throw err
    }
  }, [])

  const getOutgoingInvitations = useCallback(async (agentId: string): Promise<InvitationResponse[]> => {
    try {
      // Use the dedicated endpoint for listing agent invitations
      return await AgentManagementService.listAgentInvitations({ 
        agentId,
        statusFilter: 'pending' 
      })
    } catch (err: unknown) {
      console.error('Failed to fetch outgoing invitations:', err)
      throw err
    }
  }, [])

  const revokeInvitation = useCallback(async (agentId: string, invitationToken: string): Promise<void> => {
    try {
      await AgentManagementService.revokeInvitation({
        agentId,
        invitationToken
      })
    } catch (err: unknown) {
      console.error('Failed to revoke invitation:', err)
      throw err
    }
  }, [])

  const leaveSharedAgent = useCallback(async (agentId: string): Promise<void> => {
    try {
      await AgentManagementService.leaveSharedAgent({
        agentId
      })
      
      // Remove the agent from the shared agents list
      setSharedAgents(prev => prev.filter(agent => agent.id !== agentId))
    } catch (err: unknown) {
      console.error('Failed to leave shared agent:', err)
      throw err
    }
  }, [])

  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchInvitations('pending'),
      fetchSharedAgents()
    ])
  }, [fetchInvitations, fetchSharedAgents])

  const clearErrors = useCallback(() => {
    setInvitationsError(null)
    setSharedAgentsError(null)
  }, [])

  return {
    // Invitations
    invitations,
    invitationsLoading,
    invitationsError,
    fetchInvitations,
    
    // Shared Agents
    sharedAgents,
    sharedAgentsLoading,
    sharedAgentsError,
    fetchSharedAgents,
    leaveSharedAgent,
    
    // Permissions
    getAgentPermissions,
    
    // Outgoing Invitations
    getOutgoingInvitations,
    revokeInvitation,
    
    // Utility
    refreshAll,
    clearErrors,
  }
}